import {
  CssCustomProperty,
  CssCustomPropertyReference,
} from 'theme/colors/cssCustomPropertiesUtils';
import { kebabCase } from 'es-toolkit/compat';

export type HexColor = `#${string}`;
type CombinedName<A extends string, B extends string> = `${A}${Capitalize<B>}`;
type FamilyDefinition<
  Family extends string,
  Value extends string,
  Definition extends string = HexColor,
> = Record<CombinedName<Family, Value>, Definition>;

type ColorFamily =
  | 'royal'
  | 'lilac'
  | 'ice'
  | 'apple'
  | 'emerald'
  | 'sunglow'
  | 'blaze'
  | 'watermelon'
  | 'bubblegum';
type ColorFamilyValue = 'lighter' | 'light' | 'base' | 'dark' | 'darker';
type ColorFamilyDefinition = FamilyDefinition<ColorFamily, ColorFamilyValue>;

type ExtendedColorFamily = 'smoke';
type ExtendedColorFamilyValue = 'lightest' | ColorFamilyValue | 'darkest' | 'lightestAlpha50';
type ExtendedColorFamilyDefinition = FamilyDefinition<
  ExtendedColorFamily,
  ExtendedColorFamilyValue
>;

type Colors = ColorFamilyDefinition & ExtendedColorFamilyDefinition;

/* eslint-disable pantry/no-raw-colors */
const lightColors: Colors = {
  appleBase: '#158646',
  appleDark: '#115930',
  appleDarker: '#104226',
  appleLight: '#9EDDB7',
  appleLighter: '#F0FDF5',
  blazeBase: '#D1471C',
  blazeDark: '#9A331B',
  blazeDarker: '#632313',
  blazeLight: '#FED7AE',
  blazeLighter: '#FFF7EE',
  bubblegumBase: '#DB2576',
  bubblegumDark: '#9D154D',
  bubblegumDarker: '#691236',
  bubblegumLight: '#F9A8D3',
  bubblegumLighter: '#FDF2F8',
  emeraldBase: '#0FB984',
  emeraldDark: '#037859',
  emeraldDarker: '#053E30',
  emeraldLight: '#6EE7BA',
  emeraldLighter: '#ECFDF6',
  iceBase: '#0792B0',
  iceDark: '#155E74',
  iceDarker: '#123E4E',
  iceLight: '#A0E7EF',
  iceLighter: '#ECFEFF',
  lilacBase: '#9335E4',
  lilacDark: '#6B23A4',
  lilacDarker: '#46176A',
  lilacLight: '#EFD5F6',
  lilacLighter: '#F5EEFB',
  royalBase: '#2565E5',
  royalDark: '#1E41AB',
  royalDarker: '#182E6E',
  royalLight: '#BFDBFC',
  royalLighter: '#EFF6FF',
  smokeBase: '#A3A3A3',
  smokeDark: '#646464',
  smokeDarker: '#333333',
  smokeDarkest: '#000000',
  smokeLight: '#DFDFDF',
  smokeLighter: '#FAFAFA',
  smokeLightest: '#FFFFFF',
  smokeLightestAlpha50: '#FFFFFF80',
  sunglowBase: '#CA8A29',
  sunglowDark: '#854D1B',
  sunglowDarker: '#5A3215',
  sunglowLight: '#FCE67F',
  sunglowLighter: '#FEFCEA',
  watermelonBase: '#DC232E',
  watermelonDark: '#991920',
  watermelonDarker: '#66161A',
  watermelonLight: '#FCA5A7',
  watermelonLighter: '#FEF2F2',
};

/* eslint-disable pantry/no-raw-colors */
const darkColors: Colors = {
  appleBase: '#22C567',
  appleDark: '#86EFB0',
  appleDarker: '#DCFCE8',
  appleLight: '#166538',
  appleLighter: '#104226',
  blazeBase: '#F9722E',
  blazeDark: '#FDBA7C',
  blazeDarker: '#FFEDD7',
  blazeLight: '#9A331B',
  blazeLighter: '#632313',
  bubblegumBase: '#EC4797',
  bubblegumDark: '#F9A8D3',
  bubblegumDarker: '#FCE7F3',
  bubblegumLight: '#9D154D',
  bubblegumLighter: '#691236',
  emeraldBase: '#04966C',
  emeraldDark: '#6EE7BA',
  emeraldDarker: '#D1FAE6',
  emeraldLight: '#115E59',
  emeraldLighter: '#053E30',
  iceBase: '#05B7D2',
  iceDark: '#67E9F7',
  iceDarker: '#CFFAFE',
  iceLight: '#155E74',
  iceLighter: '#123E4E',
  lilacBase: '#A856F1',
  lilacDark: '#D8B5FB',
  lilacDarker: '#FAF5FF',
  lilacLight: '#6B23A4',
  lilacLighter: '#46176A',
  royalBase: '#4E95F3',
  royalDark: '#93C6FA',
  royalDarker: '#DBEAFD',
  royalLight: '#1E41AB',
  royalLighter: '#182E6E',
  smokeBase: '#55555A',
  smokeDark: '#95959C',
  smokeDarker: '#D2D2D2',
  smokeDarkest: '#FFFFFF',
  smokeLight: '#323236',
  smokeLighter: '#282829',
  smokeLightest: '#1C1C1E',
  smokeLightestAlpha50: '#00000080',
  sunglowBase: '#EAB336',
  sunglowDark: '#FDE05E',
  sunglowDarker: '#FEF9C7',
  sunglowLight: '#854D1B',
  sunglowLighter: '#5A3215',
  watermelonBase: '#EF4349',
  watermelonDark: '#FCA5A7',
  watermelonDarker: '#FEE2E3',
  watermelonLight: '#991920',
  watermelonLighter: '#66161A',
};

export const themeNames = ['light', 'dark'] as const;
export type ThemeName = (typeof themeNames)[number];

export const palettes: Record<ThemeName, Colors> = {
  light: lightColors,
  dark: darkColors,
};

export type ColorToken =
  | 'bgDefault'
  | 'bgDefaultAlpha50'
  | 'bgBase'
  | 'bgContrast'
  | 'bgDangerBase'
  | 'bgDangerDark'
  | 'bgDangerLighter'
  | 'bgDark'
  | 'bgDarker'
  | 'bgFeatureGateLighter'
  | 'bgFeatureGateBase'
  | 'bgFeatureGateDark'
  | 'bgInternalBase'
  | 'bgInternalDark'
  | 'bgInternalLighter'
  | 'bgLight'
  | 'bgLighter'
  | 'bgPrimaryBase'
  | 'bgPrimaryDark'
  | 'bgPrimaryLighter'
  | 'bgSuccessBase'
  | 'bgSuccessDark'
  | 'bgSuccessLighter'
  | 'bgWarningBase'
  | 'bgWarningDark'
  | 'bgWarningLighter'
  | 'borderDefault'
  | 'borderContrast'
  | 'borderDangerBase'
  | 'borderDangerDark'
  | 'borderDark'
  | 'borderFeatureGateBase'
  | 'borderFeatureGateDark'
  | 'borderInternalBase'
  | 'borderInternalDark'
  | 'borderPrimaryBase'
  | 'borderPrimaryDark'
  | 'borderSuccessBase'
  | 'borderSuccessDark'
  | 'borderWarningBase'
  | 'borderWarningDark'
  | 'fgDefault'
  | 'fgSubtle'
  | 'fgDisabled'
  | 'fgContrast'
  | 'fgPrimaryBase'
  | 'fgPrimaryDark'
  | 'fgDangerBase'
  | 'fgDangerDark'
  | 'fgWarningBase'
  | 'fgWarningDark'
  | 'fgSuccessBase'
  | 'fgSuccessDark'
  | 'fgFeatureGateBase'
  | 'fgFeatureGateDark'
  | 'fgInternalBase'
  | 'fgInternalDark';

type ColorMapping = Record<ColorToken, keyof Colors>;

const colorMapping: ColorMapping = {
  bgDefault: 'smokeLightest',
  bgDefaultAlpha50: 'smokeLightestAlpha50',
  bgBase: 'smokeBase',
  bgContrast: 'smokeDarkest',
  bgDangerBase: 'watermelonBase',
  bgDangerDark: 'watermelonDark',
  bgDangerLighter: 'watermelonLighter',
  bgDark: 'smokeDark',
  bgDarker: 'smokeDarker',
  bgFeatureGateLighter: 'lilacLighter',
  bgFeatureGateBase: 'lilacBase',
  bgFeatureGateDark: 'lilacDark',
  bgInternalBase: 'sunglowBase',
  bgInternalDark: 'sunglowDark',
  bgInternalLighter: 'sunglowLighter',
  bgLight: 'smokeLight',
  bgLighter: 'smokeLighter',
  bgPrimaryBase: 'royalBase',
  bgPrimaryDark: 'royalDark',
  bgPrimaryLighter: 'royalLighter',
  bgSuccessBase: 'appleBase',
  bgSuccessDark: 'appleDark',
  bgSuccessLighter: 'appleLighter',
  bgWarningBase: 'blazeBase',
  bgWarningDark: 'blazeDark',
  bgWarningLighter: 'blazeLighter',
  borderDefault: 'smokeLight',
  borderContrast: 'smokeLightest',
  borderDangerBase: 'watermelonBase',
  borderDangerDark: 'watermelonDark',
  borderDark: 'smokeDark',
  borderFeatureGateBase: 'lilacBase',
  borderFeatureGateDark: 'lilacDark',
  borderInternalBase: 'sunglowBase',
  borderInternalDark: 'sunglowDark',
  borderPrimaryBase: 'royalBase',
  borderPrimaryDark: 'royalDark',
  borderSuccessBase: 'appleBase',
  borderSuccessDark: 'appleDark',
  borderWarningBase: 'blazeBase',
  borderWarningDark: 'blazeDark',
  fgDefault: 'smokeDarkest',
  fgContrast: 'smokeLightest',
  fgDangerBase: 'watermelonBase',
  fgDangerDark: 'watermelonDark',
  fgFeatureGateBase: 'lilacBase',
  fgFeatureGateDark: 'lilacDark',
  fgInternalBase: 'sunglowBase',
  fgInternalDark: 'sunglowDark',
  fgSubtle: 'smokeDark',
  fgDisabled: 'smokeBase',
  fgPrimaryBase: 'royalBase',
  fgPrimaryDark: 'royalDark',
  fgSuccessBase: 'appleBase',
  fgSuccessDark: 'appleDark',
  fgWarningBase: 'blazeBase',
  fgWarningDark: 'blazeDark',
};

const paletteColors = [
  'appleBase',
  'blazeBase',
  'royalBase',
  'smokeBase',
  'smokeDark',
  'smokeDarkest',
  'smokeLight',
  'smokeLightest',
  'watermelonBase',
] as const;
type Palette = Record<(typeof paletteColors)[number], HexColor>;

export interface ThemeImplementation {
  /**
   * @deprecated These colors should only be used where MUI needs hex values. Use `colors` instead.
   */
  palette: Record<ThemeName, Palette>;

  /**
   * Themed color tokens
   */
  colors: Record<ColorToken, CssCustomPropertyReference>;
}

/**
 * Generates a color palette with a given name by resolving `palette` values to their corresponding hex codes
 * and converting color token -> color palette references to CSS custom property references
 * @param themeName - The name of the theme to process
 */
function generateThemedPalette(themeName: ThemeName): Palette {
  return paletteColors.reduce((aggregator, color) => {
    return {
      ...aggregator,
      [color]: palettes[themeName][color],
    };
  }, {} as Palette);
}

/**
 * Convert color mappings to CSS variable references and generate color palettes for each theme
 */
function generateThemes(): ThemeImplementation {
  const colors = Object.entries(colorMapping).reduce(
    (aggregator, [color, token]) => {
      return {
        ...aggregator,
        [color]: toCssVariable(token),
      };
    },
    {} as Record<ColorToken, CssCustomPropertyReference>
  );

  const palette = themeNames.reduce(
    (allThemes, themeName) => {
      return {
        ...allThemes,
        [themeName]: generateThemedPalette(themeName),
      };
    },
    {} as Record<ThemeName, Palette>
  );

  return { colors, palette };
}

/**
 * Given a camelCase-keyed object mapping named colors to hex values, return a new object where
 * anything in camelCase is rewritten in --kebab-case to be used as a CSS custom property
 */
function convertColorsToCssVariables(colors: Colors) {
  return (Object.keys(colors) as Array<keyof Colors>).reduce((cssTheme, colorName) => {
    const nameAsCss = `--${kebabCase(colorName)}`;
    return {
      ...cssTheme,
      [nameAsCss]: colors[colorName],
    };
  }, {});
}

/**
 * Convert color mappings to CSS custom properties for all themes
 */
function generateColorCssVariables() {
  return themeNames.reduce(
    (allThemes, themeName) => {
      return {
        ...allThemes,
        [themeName]: convertColorsToCssVariables(palettes[themeName]),
      };
    },
    {} as Record<ThemeName, ThemeImplementation>
  );
}

/**
 * Given a mapping of color tokens to color names, generates CSS custom properties referencing those named colors,
 * e.g. `borderContrast: smokeLightest` becomes `--border-contrast: var(--smoke-lightest)`
 */
function convertColorMappingToCssVariables() {
  return (Object.keys(colorMapping) as Array<keyof ColorMapping>).reduce((cssTheme, colorToken) => {
    const nameAsCss = `--${kebabCase(colorToken as string)}`;
    const colorAsCssVariable = toCssVariable(colorMapping[colorToken]);
    return {
      ...cssTheme,
      [nameAsCss]: colorAsCssVariable,
    };
  }, {}) as Record<CssCustomProperty, HexColor>;
}

function toCssVariable(str: string): CssCustomPropertyReference {
  return `var(--${kebabCase(str)})`;
}

// These are the exports for injection into the application
// This goes into the CSS itself and maps color tokens to named colors (e.g. `--border-contrast: var(--smoke-lightest)`)
export const cssThemeMapping = convertColorMappingToCssVariables();
// This also goes into the CSS and provides custom properties defining all named colors (e.g. `--smoke-darkest: #000000`)
export const cssColorMapping = generateColorCssVariables();

// These are JS values to reference the CSS values
export const themes = generateThemes();
